import { useLazyQuery } from '@apollo/client';
import { Box } from '@material-ui/core';
import CircularLoading from 'components/CircularLoading';
import DatePicker from 'components/DatePicker';
import DcDataGrid from 'components/DevExtremeDataGrid';
import Popper, { MessageType } from 'components/Popper';
import SectionWrapper from 'components/SectionWrapper';
import { useContextSnackbar } from 'components/Snackbar/context';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import { Field, Form, Formik, FormikProps } from 'formik';
import { DataGridRef } from 'hooks/useDataGridExporter';
import { DateTime } from 'luxon';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GET_RESIDENT_OCCUPANCY } from 'services/residentContract';
import ListHeader from '../common/ListHeader';

type OccupancyRow = {
  uniqueKey: string;
  careLevel: string;
  residents: number;
  percent: number;
  sortOrder: number;
};

const initialValues: OccupancyIndicatorQueryFilter = {
  from: DateTime.now().minus({ days: 3 }).startOf('day').toJSDate(),
  to: DateTime.now().endOf('day').toJSDate(),
};

export interface OccupancyIndicatorQueryFilter {
  from: Date;
  to: Date;
}

const OccupancyIndicator = () => {
  const { t } = useTranslation();
  const { showApiError } = useContextSnackbar();
  const tableRef = useRef<DataGrid<OccupancyRow, string>>();
  const [dataSource, setDataSource] = useState<OccupancyRow[]>([]);
  const formRef = useRef<FormikProps<OccupancyIndicatorQueryFilter>>();
  const [
    getList,
    {
      data: residentOccupancyData,
      error: residentOccupancyError,
      loading: residentOccupancyLoading,
    },
  ] = useLazyQuery(GET_RESIDENT_OCCUPANCY, {
    variables: {
      filter: { from: formRef.current?.values.from, to: formRef.current?.values.to },
    },
  });

  useEffect(() => {
    if (residentOccupancyError) {
      showApiError(residentOccupancyError);
      return;
    }
    if (residentOccupancyData) {
      const occ = residentOccupancyData.getResidentOccupancy;

      const rows: OccupancyRow[] = [
        {
          uniqueKey: 'total',
          careLevel: '0-5',
          residents: (occ.zero + occ.one + occ.two + occ.three + occ.four + occ.five).toFixed(1),
          percent: occ.totalOccupancy.toFixed(1),
          sortOrder: 0,
        },
        {
          uniqueKey: '0',
          careLevel: '0',
          residents: Number(occ.zero.toFixed(1)),
          percent: Number(occ.zeroPercent.toFixed(1)),
          sortOrder: 1,
        },
        {
          uniqueKey: '1',
          careLevel: '1',
          residents: occ.one.toFixed(1),
          percent: occ.onePercent.toFixed(1),
          sortOrder: 2,
        },
        {
          uniqueKey: '2',
          careLevel: '2',
          residents: occ.two.toFixed(1),
          percent: occ.twoPercent.toFixed(1),
          sortOrder: 3,
        },
        {
          uniqueKey: '3',
          careLevel: '3',
          residents: occ.three.toFixed(1),
          percent: occ.threePercent.toFixed(1),
          sortOrder: 4,
        },
        {
          uniqueKey: '4',
          careLevel: '4',
          residents: occ.four.toFixed(1),
          percent: occ.fourPercent.toFixed(1),
          sortOrder: 5,
        },
        {
          uniqueKey: '5',
          careLevel: '5',
          residents: occ.five.toFixed(1),
          percent: occ.fivePercent.toFixed(1),
          sortOrder: 6,
        },
      ];

      setDataSource(rows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residentOccupancyData, residentOccupancyError]);

  const onRefresh = () => {
    getList({
      variables: {
        filter: {
          from: formRef.current?.values.from,
          to: formRef.current?.values.to,
        },
      },
    });
  };

  return (
    <>
      <ListHeader title={t('lists_residentOccupancy')} handleClickMainButton={onRefresh}>
        <Formik
          onSubmit={onRefresh}
          initialValues={initialValues}
          innerRef={formRef as unknown as React.Ref<FormikProps<OccupancyIndicatorQueryFilter>>}
        >
          <Form>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <Field
                component={DatePicker}
                maxDateTime={new Date()}
                name={'from'}
                label={t('lists_nursingOverview_from')}
                editable={true}
                inputFormat="dd.MM.yyyy"
                wrapper={{ py: 0, width: '200px', mr: '.5rem' }}
              />
              <Field
                component={DatePicker}
                maxDateTime={new Date()}
                name={'to'}
                label={t('lists_nursingOverview_to')}
                editable={true}
                inputFormat="dd.MM.yyyy"
                wrapper={{ py: 0, width: '200px', mr: '.5rem' }}
              />
            </Box>
          </Form>
        </Formik>
      </ListHeader>
      {residentOccupancyLoading ? (
        <CircularLoading />
      ) : (
        <SectionWrapper positionRelative={true}>
          <Popper
            id="data-grid-info"
            type={MessageType.INFO}
            msg={t('lists_dataGridInfo')}
            direction="right-start"
            isLongString={true}
            style={{ position: 'absolute', left: '10px', top: '10px', padding: 0 }}
          />
          <DcDataGrid
            dataSource={dataSource}
            rowKeyProperty="uniqueKey"
            showRowNumbers={false}
            tableRef={tableRef as unknown as DataGridRef}
            allowGrouping={false}
            wordWrapEnabled={true}
          >
            <Column
              dataField="careLevel"
              caption={t('lists_residentOccupancy_careLevel')}
              sortOrder="asc"
              sortIndex={0}
              calculateSortValue="sortOrder"
            />
            <Column
              dataField="residents"
              caption={t('lists_residentOccupancy_numberOfResidents')}
            />
            <Column
              dataField="percent"
              caption={t('lists_residentOccupancy_percentage')}
              cellRender={(cellData: any) => `${cellData.value}%`}
            />
          </DcDataGrid>
        </SectionWrapper>
      )}
    </>
  );
};

export default OccupancyIndicator;
